/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { useNetworkCompanyOptions } from '@/hooks/useDropdowns';
import { useFeatures } from '@/hooks/useFeatures';
import { useTranslator } from '@/hooks/useTranslator';
import { PLATFORM_FEATURES } from '@/lib/constants/features';
import { queryClient } from '@/lib/utils/queryClient';
import { useSessionStore } from '@/store/sessionStore';
import { dateRangeRenderFilter } from '../../../-components/dateRangeRenderFilter';
import { ReportToolbar } from '../../../-components/ReportToolbar';
import { CompletionByDepthChart } from '../../../-components/tracing-compliance/CompletionByDepthChart';
import { DocPerOrderSection } from '../../../-components/tracing-compliance/DocPerOrderSection';
import { useReportFilterOptions } from '../../../-hooks/useFilterOptions';
import { useReportFilterPreferences } from '../../../-hooks/useReportFilterPreferences';
import { useReportFilters } from '../../../-hooks/useReportFilters';
import {
  tracingDepthCompletionQueryOptions,
  useTracingDepthCompletionQuery,
} from '../../../-hooks/useTracingComplianceQuery';
import { defaultDateRange } from '../../../-utils/dateUtils';
import { buildComplianceFilterDefs } from '../../../-utils/filterDefs';

export const Route = createFileRoute(
  '/(client)/report/(pages)/(analytics)/tracing-compliance/',
)({
  validateSearch: z.object({
    filter: z.string().optional(),
    docsTab: z.enum(['missing', 'uploaded']).optional(),
  }),
  loaderDeps: ({ search }) => ({ filter: search.filter }),
  loader: ({ deps }) => {
    const companyId = useSessionStore.getState().user?.companyId ?? '';
    if (!companyId) return;
    const filter = deps.filter ?? JSON.stringify(defaultDateRange());
    return queryClient.ensureQueryData(
      tracingDepthCompletionQueryOptions(companyId, filter),
    );
  },
  component: TracingCompliancePage,
});

function TracingCompliancePage() {
  const { isFeatureEnabled } = useFeatures();
  const { getTranslation: t } = useTranslator({ page: 'tracingCompliance' });

  const { processOptions, countryOptions } = useReportFilterOptions();
  const suppliers = useNetworkCompanyOptions();
  const filterDefs = buildComplianceFilterDefs({
    processOptions,
    countryOptions,
    t,
    dateRangeRenderFilter,
  });

  const { filters, setFilters, appliedFilter } = useReportFilters({
    supplierOptions: [],
    processOptions,
    countryOptions,
  });

  const { saveFilter } = useReportFilterPreferences('rpt-tracing-compliance');
  useEffect(() => {
    saveFilter(appliedFilter);
  }, [appliedFilter, saveFilter]);

  const { data, isLoading } = useTracingDepthCompletionQuery(appliedFilter);

  if (!isFeatureEnabled(PLATFORM_FEATURES.CASCADE_TRACING)) {
    return null;
  }

  return (
    <PageContainer className='flex flex-col overflow-hidden'>
      <div className='shrink-0 border-b border-border/60 bg-background px-4 py-3 z-10'>
        <ReportToolbar
          filterDefs={filterDefs}
          filters={filters}
          onFiltersChange={setFilters}
          helpUrl='https://app.intercom.com/a/apps/10750187-introduction-to-the-tracing-compliance-report'
        />
      </div>

      <div className='flex-1 min-h-0 overflow-y-auto'>
        <div className='flex flex-col gap-6 p-4 pb-10'>
          <CompletionByDepthChart data={data} loading={isLoading} />
          <DocPerOrderSection
            filter={appliedFilter}
            supplierOptions={suppliers.data ?? []}
          />
        </div>
      </div>
    </PageContainer>
  );
}
